import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Loader, Upload, User, MapPin,
  Lock, Edit3, AlertCircle, RefreshCw,
  Building
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API = 'https://tradebot-k4nt.onrender.com/api';

/* ── fetch live Stripe account ── */
const getAccountStatus = async (username) => {
  const res = await fetch(`${API}/payment/retive-stripe-account-info/web/${username}`);
  return res.json();
};

/* ── currency config (mirrors ConnectAccount) ── */
const CURRENCY_CFG = {
  USD: {
    accType: 'USD', country: 'US', label: 'USD', flag: '🇺🇸',
    accent: '#16a34a', accentLight: '#dcfce7', accentText: '#14532d',
    steps: ['personal', 'address', 'ssn', 'account'],
    hasSsn: true, hasUploadStep: false,
    routerLabel: 'Routing Number',
  },
  GBP: {
    accType: 'GDP', country: 'GB', label: 'GBP', flag: '🇬🇧',
    accent: '#2563eb', accentLight: '#dbeafe', accentText: '#1e3a8a',
    steps: ['personal', 'address', 'upload', 'account'],
    hasSsn: false, hasUploadStep: true,
    routerLabel: 'Sort Code',
  },
  YEN: {
    accType: 'YEN', country: 'JP', label: 'YEN', flag: '🇯🇵',
    accent: '#dc2626', accentLight: '#fee2e2', accentText: '#7f1d1d',
    steps: ['personal', 'address', 'upload', 'account'],
    hasSsn: false, hasUploadStep: true,
    routerLabel: 'Router Number',
  },
};

const STEP_META = {
  personal: { label: 'Personal Info',    icon: User },
  address:  { label: 'Address',          icon: MapPin },
  upload:   { label: 'Identity Docs',    icon: Upload },
  ssn:      { label: 'SSN',              icon: Lock },
  account:  { label: 'Bank / Card',      icon: Building },
};

/* ── determine which steps the user has already completed ── */
const deriveCompletedSteps = (stripeAccount, cfg) => {
  if (!stripeAccount || stripeAccount === 2) return [];

  const completed = [];
  const req = stripeAccount.requirements || {};
  // eslint-disable-next-line no-unused-vars
  const allDue = [...new Set([
    ...(req.past_due || []),
    ...(req.currently_due || []),
    ...(req.eventually_due || []),
  ])];
  const ind = stripeAccount.individual || {};

  // Personal: first name exists
  if (ind.first_name || ind.first_name_kanji) {
    completed.push('personal');
  }

  // Address: address fields submitted
  const addrSrc = cfg.accType === 'YEN' ? ind.address_kanji : ind.address;
  if (addrSrc?.city || addrSrc?.line1) {
    completed.push('address');
  }

  // Upload: verification doc front is not null
  const docFront = ind?.verification?.document?.front;
  if (cfg.hasUploadStep && docFront !== null && docFront !== undefined) {
    completed.push('upload');
  }

  // SSN (USD only): ssn_last_4_provided
  if (cfg.hasSsn && ind.ssn_last_4_provided) {
    completed.push('ssn');
  }

  // Bank account: at least one external account
  const extCount = stripeAccount.external_accounts?.total_count || 0;
  if (extCount > 0) {
    completed.push('account');
  }

  return completed;
};

/* ── small reusable field ── */
const Field = ({ label, id, type = 'text', value, onChange, placeholder, required = true }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input
      id={id} type={type} value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder} required={required}
      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
    />
  </div>
);

const FileField = ({ label, id, inputRef }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <div className="relative border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-slate-400 transition-colors cursor-pointer"
      onClick={() => inputRef.current?.click()}>
      <Upload size={18} className="mx-auto mb-1 text-slate-400" />
      <p className="text-xs text-slate-500">Click to select file</p>
      <input ref={inputRef} id={id} type="file" accept="image/*,.pdf" className="hidden" />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function UpdateAccount() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user: ctxUser } = useUser();

  const rawCurrency = (params.get('currency') || 'USD').toUpperCase();
  const currency = CURRENCY_CFG[rawCurrency] ? rawCurrency : 'USD';
  const cfg = CURRENCY_CFG[currency];

  const [pageLoading,  setPageLoading]  = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [activeStep,   setActiveStep]   = useState(null);   // which edit panel is open
  const [completed,    setCompleted]    = useState([]);     // steps user has done
  const [stripeData,   setStripeData]   = useState(null);   // raw Stripe account object
  const [docMode,      setDocMode]      = useState('drive');

  /* ── file refs ── */
  const fCoverRef  = useRef(null);
  const bCoverRef  = useRef(null);
  const idCoverRef = useRef(null);

  /* ── personal fields ── */
  const [fname,     setFname]     = useState('');
  const [lname,     setLname]     = useState('');
  const [fnameKana, setFnameKana] = useState('');
  const [lnameKana, setLnameKana] = useState('');
  const [email,     setEmail]     = useState('');
  const [phone,     setPhone]     = useState('');
  const [dob,       setDob]       = useState('');

  /* ── address fields ── */
  const [addrCity,  setAddrCity]  = useState('');
  const [addrLoc,   setAddrLoc]   = useState('');
  const [addrState, setAddrState] = useState('');
  const [postCode,  setPostCode]  = useState('');
  const [blockNum,  setBlockNum]  = useState('');
  const [buildName, setBuildName] = useState('');
  const [buildNameJP, setBuildNameJP] = useState('');

  /* ── SSN fields ── */
  const [ssnLast4,  setSsnLast4]  = useState('');
  const [idNumSSN,  setIdNumSSN]  = useState('');

  /* ── account fields ── */
  const [rNum,        setRNum]        = useState('');
  const [aNum,        setANum]        = useState('');
  const [accHoldName, setAccHoldName] = useState('');
  const [cardNum,     setCardNum]     = useState('');
  const [cardMonth,   setCardMonth]   = useState('');
  const [cardYear,    setCardYear]    = useState('');
  const [cardCVC,     setCardCVC]     = useState('');
  const [accTab,      setAccTab]      = useState('bank');

  /* ── load Stripe data and pre-fill ── */
  const loadStatus = useCallback(async () => {
    if (!ctxUser?.telegramName) return;
    setPageLoading(true);
    try {
      const data = await getAccountStatus(ctxUser.telegramName);
      const key = currency === 'GBP' ? 'retrieveGBP'
                : currency === 'YEN' ? 'retrieveYEN'
                : 'retrieveUSD';
      const acct = data?.allAccountObj?.[key];
      setStripeData(acct);
      setCompleted(deriveCompletedSteps(acct, cfg));

      /* pre-fill fields from existing Stripe data */
      if (acct && acct !== 2) {
        const ind = acct.individual || {};
        setEmail(acct.email || ctxUser.email || '');
        setPhone(ind.phone || '');
        setFname(ind.first_name || ind.first_name_kanji || '');
        setLname(ind.last_name  || ind.last_name_kanji  || '');
        setFnameKana(ind.first_name_kana || '');
        setLnameKana(ind.last_name_kana  || '');
        if (ind.dob) {
          const d = ind.dob;
          setDob(`${d.year}-${String(d.month).padStart(2,'0')}-${String(d.day).padStart(2,'0')}`);
        }
        const addr = cfg.accType === 'YEN' ? ind.address_kanji : ind.address;
        if (addr) {
          setAddrCity(addr.city   || '');
          setAddrLoc(addr.line1   || '');
          setAddrState(addr.state || '');
          setPostCode(addr.postal_code || '');
          if (cfg.accType === 'YEN') {
            setBuildName(addr.town || '');
            if (ind.address_kana) {
              setBlockNum(ind.address_kana.line1 || '');
              setBuildNameJP(ind.address_kana.town || '');
            }
          }
        }
      } else {
        setEmail(ctxUser.email || '');
      }
    } catch {
      toast.error('Could not load account status');
    } finally {
      setPageLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxUser?.telegramName, currency, cfg]);

  useEffect(() => {
    if (!ctxUser?.telegramName) { navigate('/login'); return; }
    loadStatus();
  }, [ctxUser?.telegramName, navigate, loadStatus]);

  /* ── error helper ── */
  const handleApiError = (d) => {
    const msg = typeof d === 'string' ? d : (d?.error?.raw?.message || 'An error occurred');
    toast.error(msg);
  };

  /* ══════════ SUBMIT HANDLERS (same API as ConnectAccount) ══════════ */

  const submitPersonal = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const regBody = { email, username: ctxUser.telegramName,
        country: cfg.country === 'GB' ? 'GB' : cfg.country === 'JP' ? 'JP' : 'US' };
      const regRes = await fetch(`${API}/payment/register-account-web`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(regBody),
      });
      const regData = await regRes.json();
      if (regRes.status !== 200) { handleApiError(regData); return; }

      let updateBody;
      if (cfg.accType === 'YEN') {
        updateBody = {
          email, username: ctxUser.telegramName, country: 'JP', businessType: 'individual',
          business_profile: { mcc: '6012', url: 'https://modanmic-fintech.onrender.com',
            product_description: 'MODAMIC Fintech、ソフトウェア製品' },
          individual: {
            first_name_kanji: fname, first_name_kana: fnameKana,
            last_name_kanji: lname, last_name_kana: lnameKana,
            dob: { day: Number(dob.split('-')[2]), month: Number(dob.split('-')[1]), year: Number(dob.split('-')[0]) },
            phone,
          },
        };
      } else {
        updateBody = {
          email, username: ctxUser.telegramName,
          country: cfg.country, businessType: 'individual',
          business_profile: { mcc: '6012', url: 'https://modanmic-fintech.onrender.com' },
          individual: {
            first_name: fname, last_name: lname,
            dob: { day: Number(dob.split('-')[2]), month: Number(dob.split('-')[1]), year: Number(dob.split('-')[0]) },
            phone,
          },
        };
      }
      const updRes = await fetch(`${API}/payment/update-account-web`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(updateBody),
      });
      const updData = await updRes.json();
      if (updRes.status !== 200) { handleApiError(updData); return; }
      toast.success('Personal info updated ✓');
      setActiveStep(null);
      await loadStatus();
    } catch { handleApiError('NETWORK ERROR'); }
    finally { setSubmitting(false); }
  };

  const submitAddress = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let addrBody;
      if (cfg.accType === 'USD') {
        addrBody = { username: ctxUser.telegramName, country: 'US',
          individual: { address: { city: addrCity, country: 'US', line1: addrLoc, line2: null, postal_code: postCode, state: addrState } } };
      } else if (cfg.accType === 'GDP') {
        addrBody = { username: ctxUser.telegramName, country: 'GB',
          individual: { address: { city: addrCity, country: 'GB', line1: addrLoc, line2: null, postal_code: postCode, state: addrState } } };
      } else {
        addrBody = { username: ctxUser.telegramName, country: 'JP',
          individual: {
            address_kanji: { city: addrCity, country: 'JP', line1: addrLoc, town: buildName, postal_code: postCode, state: addrState },
            address_kana:  { line1: blockNum, town: buildNameJP },
          },
          company: { address_kana: { line1: blockNum } },
        };
      }
      const res  = await fetch(`${API}/payment/update-account-web/address`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(addrBody),
      });
      const data = await res.json();
      if (res.status !== 200) { handleApiError(data); return; }
      toast.success('Address updated ✓');
      setActiveStep(null);
      await loadStatus();
    } catch { handleApiError('NETWORK ERROR'); }
    finally { setSubmitting(false); }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fFile  = fCoverRef.current?.files[0];
      const bFile  = bCoverRef.current?.files[0];
      const idFile = idCoverRef.current?.files[0];

      if (docMode === 'drive') {
        if (!fFile || !bFile) { toast.error('Please select both front and back covers'); return; }
        const fd = new FormData();
        fd.append('fCoverGDP', fFile);
        fd.append('bCoverGDP', bFile);
        fd.append('username', ctxUser.telegramName);
        fd.append('country', cfg.country);
        const res = await fetch(`${API}/payment/update-account-web/upload/front-back-corver-gbp`, { method: 'POST', body: fd });
        const data = await res.json();
        if (res.status !== 200) { handleApiError(data); return; }
      } else {
        if (!idFile) { toast.error('Please select an ID document'); return; }
        const fd = new FormData();
        fd.append('idCoverGDP', idFile);
        fd.append('username', ctxUser.telegramName);
        fd.append('country', cfg.country);
        const res = await fetch(`${API}/payment/update-account-web/upload/id-cover-gbp`, { method: 'POST', body: fd });
        const data = await res.json();
        if (res.status !== 200) { handleApiError(data); return; }
      }
      toast.success('Documents updated ✓');
      setActiveStep(null);
      await loadStatus();
    } catch { handleApiError('NETWORK ERROR'); }
    finally { setSubmitting(false); }
  };

  const submitSSN = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res  = await fetch(`${API}/payment/update-account-web/ssn`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: ctxUser.telegramName, country: 'US',
          individual: { ssn_last_4: ssnLast4, id_number: idNumSSN } }),
      });
      const data = await res.json();
      if (res.status !== 200) { handleApiError(data); return; }
      toast.success('SSN updated ✓');
      setActiveStep(null);
      await loadStatus();
    } catch { handleApiError('NETWORK ERROR'); }
    finally { setSubmitting(false); }
  };

  const submitBank = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let body;
      if (cfg.accType === 'USD')       body = { username: ctxUser.telegramName, country: 'US', accountNumber: aNum, routingNumber: rNum };
      else if (cfg.accType === 'GDP')  body = { username: ctxUser.telegramName, country: 'GB', accountNumber: aNum, routingNumber: rNum };
      else                             body = { username: ctxUser.telegramName, country: 'JP', accountNumber: aNum, routingNumber: rNum, accHoldNameYEN: accHoldName };
      const res  = await fetch(`${API}/payment/add-account-number/web`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.status !== 200) { handleApiError(data); return; }
      toast.success('Bank account updated ✓');
      setActiveStep(null);
      await loadStatus();
    } catch { handleApiError('NETWORK ERROR'); }
    finally { setSubmitting(false); }
  };

  const submitCard = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { username: ctxUser.telegramName, country: cfg.country,
        exp_month: cardMonth, exp_year: cardYear, cardNumber: cardNum, cvc: cardCVC };
      const res  = await fetch(`${API}/payment/add-card-payout-web`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.status !== 200) { handleApiError(data); return; }
      toast.success('Card updated ✓');
      setActiveStep(null);
      await loadStatus();
    } catch { handleApiError('NETWORK ERROR'); }
    finally { setSubmitting(false); }
  };

  /* ══════════ RENDER HELPERS ══════════ */

  const SubmitBtn = ({ children }) => (
    <button type="submit" disabled={submitting}
      className="w-full py-3 rounded-xl text-white font-bold flex items-center justify-center gap-2 transition-all shadow-sm disabled:opacity-60"
      style={{ background: cfg.accent }}>
      {submitting ? <><Loader size={16} className="animate-spin" /> Saving…</> : children}
    </button>
  );

  const acct = stripeData && stripeData !== 2 ? stripeData : null;
  // eslint-disable-next-line no-unused-vars
  const ind  = acct?.individual || null;

  /* ── loading ── */
  if (pageLoading) return (
    <DashboardLayout>
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin h-10 w-10 mx-auto mb-3" style={{ color: cfg.accent }} />
          <p className="text-slate-600 font-medium">Loading account details…</p>
        </div>
      </div>
    </DashboardLayout>
  );

  /* ── list of steps to show ── */
  const visibleSteps = cfg.steps.filter(s => !(s === 'upload' && !cfg.hasUploadStep));

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/account-verification')}
            className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{cfg.flag}</span>
              <h1 className="text-2xl font-bold text-slate-900">Update {cfg.label} Account</h1>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">@{ctxUser?.telegramName}</p>
          </div>
          <button onClick={loadStatus} title="Refresh"
            className="ml-auto p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors">
            <RefreshCw size={18} />
          </button>
        </div>

        {/* Account status banner */}
        {acct && (
          <div className="rounded-2xl p-4 mb-6 flex items-center gap-3 text-sm font-medium"
            style={{ background: cfg.accentLight, color: cfg.accentText }}>
            {acct.charges_enabled && acct.payouts_enabled
              ? <><CheckCircle size={18} /> Account fully verified — charges &amp; payouts enabled</>
              : <><AlertCircle size={18} /> Account connected — some steps may still be pending</>
            }
          </div>
        )}

        {/* Step cards */}
        <div className="space-y-3">
          {visibleSteps.map((step) => {
            const meta       = STEP_META[step];
            const Icon       = meta.icon;
            const isDone     = completed.includes(step);
            // A step is accessible if it is the first step OR the previous step is completed
            const stepIndex  = visibleSteps.indexOf(step);
            const prevStep   = stepIndex > 0 ? visibleSteps[stepIndex - 1] : null;
            const accessible = stepIndex === 0 || (prevStep && completed.includes(prevStep));
            const isOpen     = activeStep === step;

            return (
              <div key={step}
                className={`rounded-2xl border-2 overflow-hidden transition-all ${
                  isOpen ? 'border-slate-300 shadow-md' : 'border-slate-200'
                } ${!accessible ? 'opacity-50' : ''}`}>

                {/* Step header row */}
                <button
                  type="button"
                  disabled={!accessible}
                  onClick={() => setActiveStep(isOpen ? null : step)}
                  className="w-full flex items-center gap-4 p-5 text-left bg-white hover:bg-slate-50 transition-colors disabled:cursor-not-allowed"
                >
                  {/* Status dot */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: isDone ? cfg.accentLight : '#f1f5f9' }}>
                    {isDone
                      ? <CheckCircle size={20} style={{ color: cfg.accent }} />
                      : <Icon size={20} className="text-slate-400" />
                    }
                  </div>

                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 text-sm">{meta.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {!accessible
                        ? `Complete "${STEP_META[prevStep]?.label}" first`
                        : isDone
                        ? 'Completed — click to update'
                        : 'Not yet submitted — click to add'
                      }
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {isDone && (
                      <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ background: cfg.accentLight, color: cfg.accentText }}>
                        <CheckCircle size={10} /> Done
                      </span>
                    )}
                    {accessible && (
                      <div className="p-1.5 rounded-lg" style={{ background: cfg.accentLight }}>
                        <Edit3 size={14} style={{ color: cfg.accent }} />
                      </div>
                    )}
                    {!accessible && <Lock size={16} className="text-slate-300" />}
                  </div>
                </button>

                {/* ── Edit panel (collapsible) ── */}
                {isOpen && accessible && (
                  <div className="border-t border-slate-100 bg-slate-50 p-5">

                    {/* PERSONAL */}
                    {step === 'personal' && (
                      <form onSubmit={submitPersonal} className="space-y-4">
                        <p className="text-xs text-slate-500 mb-3">Update your personal information below.</p>
                        <Field label="Email" id="upd-email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" />
                        {cfg.accType === 'YEN' ? (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <Field label="First Name (Kanji)" id="upd-fname" value={fname} onChange={setFname} placeholder="山田" />
                              <Field label="Last Name (Kanji)" id="upd-lname" value={lname} onChange={setLname} placeholder="花子" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <Field label="First Name (Kana)" id="upd-fkana" value={fnameKana} onChange={setFnameKana} placeholder="ヤマダ" />
                              <Field label="Last Name (Kana)" id="upd-lkana" value={lnameKana} onChange={setLnameKana} placeholder="ハナコ" />
                            </div>
                          </>
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            <Field label="First Name" id="upd-fname" value={fname} onChange={setFname} placeholder="John" />
                            <Field label="Last Name" id="upd-lname" value={lname} onChange={setLname} placeholder="Doe" />
                          </div>
                        )}
                        <Field label="Date of Birth" id="upd-dob" type="date" value={dob} onChange={setDob} placeholder="" />
                        <Field label="Phone" id="upd-phone" type="tel" value={phone} onChange={setPhone} placeholder="+1 555 000 0000" />
                        <SubmitBtn>Save Personal Info</SubmitBtn>
                      </form>
                    )}

                    {/* ADDRESS */}
                    {step === 'address' && (
                      <form onSubmit={submitAddress} className="space-y-4">
                        <p className="text-xs text-slate-500 mb-3">Update your address details.</p>
                        <Field label="City" id="upd-city" value={addrCity} onChange={setAddrCity} placeholder="New York" />
                        <Field label="Street Address" id="upd-line1" value={addrLoc} onChange={setAddrLoc} placeholder="123 Main St" />
                        <div className="grid grid-cols-2 gap-3">
                          <Field label="State / Province" id="upd-state" value={addrState} onChange={setAddrState} placeholder="NY" />
                          <Field label="Postal Code" id="upd-post" value={postCode} onChange={setPostCode} placeholder="10001" />
                        </div>
                        {cfg.accType === 'YEN' && (
                          <>
                            <Field label="Building / Unit Number" id="upd-block" value={blockNum} onChange={setBlockNum} placeholder="Block / kana line1" />
                            <Field label="Building Name (Kanji)" id="upd-build" value={buildName} onChange={setBuildName} placeholder="Town (kanji)" />
                            <Field label="Building Name (Kana)" id="upd-buildJP" value={buildNameJP} onChange={setBuildNameJP} placeholder="Town (kana)" />
                          </>
                        )}
                        <SubmitBtn>Save Address</SubmitBtn>
                      </form>
                    )}

                    {/* UPLOAD (GBP + YEN) */}
                    {step === 'upload' && cfg.hasUploadStep && (
                      <form onSubmit={submitUpload} className="space-y-4">
                        <p className="text-xs text-slate-500 mb-3">Upload an updated identity document.</p>
                        <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                          {['drive', 'id'].map(m => (
                            <button key={m} type="button" onClick={() => setDocMode(m)}
                              className="flex-1 py-2 text-sm font-medium transition-all"
                              style={{ background: docMode === m ? cfg.accent : '', color: docMode === m ? '#fff' : '#475569' }}>
                              {m === 'drive' ? "Driver's Licence" : 'ID Card'}
                            </button>
                          ))}
                        </div>
                        {docMode === 'drive'
                          ? <><FileField label="Front Cover" id="upd-fCover" inputRef={fCoverRef} /><FileField label="Back Cover" id="upd-bCover" inputRef={bCoverRef} /></>
                          : <FileField label="ID Card" id="upd-idCover" inputRef={idCoverRef} />
                        }
                        <SubmitBtn>Upload Documents</SubmitBtn>
                      </form>
                    )}

                    {/* SSN (USD only) */}
                    {step === 'ssn' && cfg.hasSsn && (
                      <form onSubmit={submitSSN} className="space-y-4">
                        <p className="text-xs text-slate-500 mb-3">Update your SSN details.</p>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1 text-center">SSN Last 4 Digits</label>
                          <input type="number" maxLength={4} value={ssnLast4} onChange={e => setSsnLast4(e.target.value)}
                            placeholder="1234" required
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-center text-2xl tracking-widest" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1 text-center">Full SSN</label>
                          <input type="number" value={idNumSSN} onChange={e => setIdNumSSN(e.target.value)}
                            placeholder="123-45-6789" required
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-center" />
                        </div>
                        <SubmitBtn>Save SSN</SubmitBtn>
                      </form>
                    )}

                    {/* BANK / CARD */}
                    {step === 'account' && (
                      <div className="space-y-4">
                        <p className="text-xs text-slate-500 mb-3">Update your linked bank account or debit card.</p>
                        {/* Sub-tab */}
                        <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                          {['bank', 'card'].map(t => (
                            <button key={t} type="button" onClick={() => setAccTab(t)}
                              className="flex-1 py-2 text-sm font-semibold transition-all"
                              style={{ background: accTab === t ? cfg.accent : '', color: accTab === t ? '#fff' : '#475569' }}>
                              {t === 'bank' ? 'Bank Account' : 'Debit Card'}
                            </button>
                          ))}
                        </div>

                        {accTab === 'bank' && (
                          <form onSubmit={submitBank} className="space-y-4">
                            {cfg.accType === 'YEN' && (
                              <Field label="Account Holder Name" id="upd-holdName" value={accHoldName} onChange={setAccHoldName} placeholder="ヤマダハナコ" />
                            )}
                            <Field label={cfg.routerLabel} id="upd-rnum" value={rNum} onChange={setRNum} placeholder="xxxx-xxxx-xx" />
                            <Field label="Account Number" id="upd-anum" type="number" value={aNum} onChange={setANum} placeholder="0123456789" />
                            <SubmitBtn>Update Bank Account</SubmitBtn>
                          </form>
                        )}
                        {accTab === 'card' && (
                          <form onSubmit={submitCard} className="space-y-4">
                            <Field label="Card Number" id="upd-cardnum" value={cardNum} onChange={setCardNum} placeholder="4242 4242 4242 4242" />
                            <div className="grid grid-cols-3 gap-3">
                              <Field label="Exp Month" id="upd-mon" type="number" value={cardMonth} onChange={setCardMonth} placeholder="MM" />
                              <Field label="Exp Year"  id="upd-yr"  type="number" value={cardYear}  onChange={setCardYear}  placeholder="YYYY" />
                              <Field label="CVC"       id="upd-cvc" type="number" value={cardCVC}   onChange={setCardCVC}   placeholder="123" />
                            </div>
                            <SubmitBtn>Update Card</SubmitBtn>
                          </form>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Stripe disclaimer */}
        <p className="text-xs text-slate-400 text-center mt-6">
          🔒 All updates are processed securely via Stripe. Changes may take a few minutes to reflect.
        </p>
      </div>
    </DashboardLayout>
  );
}
